import { LeaveAreaCode, PermissionCode } from '@gb-ticket/shared-types';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { leavePanelClassName } from '@/products/leave/features/leave-panel';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import {
  LeaveCalendarDayList,
  LeaveCalendarLegend,
  LeaveCalendarMonth,
} from '@/products/leave/features/leave-calendar-month';
import {
  berlinTodayIso,
  filterCalendarDays,
  filterYearAbsences,
  formatGermanDate,
  hasAnyAbsence,
  monthBounds,
  monthLabel,
} from '@/products/leave/features/leave-calendar-utils';
import { GERMAN_STATE_LABEL } from '@/products/leave/features/leave-labels';
import { useLeaveAreas } from '@/products/leave/features/use-leave-areas';
import { useLeaveRangePick } from '@/products/leave/features/use-leave-range-pick';
import {
  LeaveYearGrid,
  LeaveYearMonthList,
  LeaveYearTotals,
} from '@/products/leave/features/leave-year-grid';
import {
  useLeaveAbsenceTypes,
  useLeaveCalendar,
  useLeaveCalendarDepartments,
  useLeaveCalendarYear,
  useLeavePeople,
} from '@/products/leave/features/use-leave';

type CalendarMode = 'year' | 'month';

export function LeaveCalendarPage() {
  const copy = useVoiceCopy();
  const me = useMe();
  const canSwitchDepartment = hasPermission(me.data, PermissionCode.LEAVE_REQUESTS_DECIDE_ALL);
  const canViewOtherYear =
    canSwitchDepartment || hasPermission(me.data, PermissionCode.LEAVE_BALANCES_ADJUST);
  const today = berlinTodayIso();
  const [year, setYear] = useState(() => Number(today.slice(0, 4)));
  const [month, setMonth] = useState(() => Number(today.slice(5, 7)));
  const [mode, setMode] = useState<CalendarMode>('year');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [kindFilter, setKindFilter] = useState('');
  const [yearStatus, setYearStatus] = useState<'' | 'approved' | 'submitted'>('');
  const [nameQuery, setNameQuery] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [yearUserId, setYearUserId] = useState<string | null>(null);
  const [yearUserName, setYearUserName] = useState<string | null>(null);
  const [personQuery, setPersonQuery] = useState('');
  const bounds = useMemo(() => monthBounds(year, month), [year, month]);
  const types = useLeaveAbsenceTypes();
  const areas = useLeaveAreas();
  const people = useLeavePeople(personQuery);
  const departments = useLeaveCalendarDepartments(
    canSwitchDepartment && mode === 'month' && filtersOpen,
  );
  const calendar = useLeaveCalendar(
    mode === 'month' ? bounds.from : '',
    mode === 'month' ? bounds.to : '',
    departmentId || null,
  );
  const yearCalendar = useLeaveCalendarYear(year, yearUserId, mode === 'year');
  const range = useLeaveRangePick();

  usePageTitle(mode === 'year' ? `Kalender ${year}` : `Kalender ${monthLabel(year, month)}`);

  function shiftMonth(delta: number) {
    const date = new Date(Date.UTC(year, month - 1 + delta, 1));
    setYear(date.getUTCFullYear());
    setMonth(date.getUTCMonth() + 1);
  }

  function goToday() {
    const nextYear = Number(today.slice(0, 4));
    const nextMonth = Number(today.slice(5, 7));
    setYear(nextYear);
    setMonth(nextMonth);
    if (mode === 'year') {
      requestAnimationFrame(() => {
        document
          .getElementById(`leave-year-month-${nextMonth}`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      });
    }
  }

  useEffect(() => {
    if (mode !== 'month') {
      return;
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        shiftMonth(-1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        shiftMonth(1);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  });

  const catalog = types.data?.items ?? [];
  const selectedType = catalog.find((type) => type.id === kindFilter) ?? null;
  const days = filterCalendarDays(calendar.data?.days ?? [], selectedType, nameQuery);
  const emptyMonth = calendar.data && !hasAnyAbsence(days);
  const yearData = yearCalendar.data;
  const visibleAbsences = filterYearAbsences(yearData?.absences ?? [], kindFilter, yearStatus);
  const linkOwnRequests = me.data?.id != null && me.data.id === yearData?.userId;
  const schoolAreaOn =
    areas.data?.items.find((item) => item.code === LeaveAreaCode.SCHOOL_HOLIDAYS)?.enabled ?? false;
  const activeError = mode === 'year' ? yearCalendar.error : calendar.error;
  const activeFailed = mode === 'year' ? yearCalendar.isError : calendar.isError;
  const schoolSpans = yearData?.schoolHolidays ?? [];
  const schoolState = yearData?.stateCode != null ? GERMAN_STATE_LABEL[yearData.stateCode] : null;
  const yearButtons = [year - 1, year, year + 1];

  return (
    <div className="space-y-6">
      <PlatformPageHeader
        title="Abwesenheitskalender"
        description={
          mode === 'year'
            ? (yearData?.displayName ?? copy('leave.calendar.personal-year'))
            : calendar.data
              ? calendar.data.departmentName
              : copy('leave.calendar.department')
        }
        action={
          <Button type="button" variant="outline" onClick={goToday}>
            Heute
          </Button>
        }
      />
      {activeFailed ? (
        <p className="text-sm text-destructive">
          {activeError instanceof ApiRequestError
            ? activeError.message
            : 'Der Kalender konnte nicht geladen werden.'}
        </p>
      ) : (
        <div className="space-y-6">
          <div className={cn(leavePanelClassName, 'flex flex-wrap items-end gap-3')}>
            {mode === 'year' ? (
              <div className="flex rounded-md border">
                {yearButtons.map((value, index) => (
                  <Button
                    key={value}
                    type="button"
                    variant={value === year ? 'default' : 'ghost'}
                    className={
                      index === 0
                        ? 'rounded-r-none'
                        : index === yearButtons.length - 1
                          ? 'rounded-l-none'
                          : 'rounded-none'
                    }
                    onClick={() => setYear(value)}
                  >
                    {value}
                  </Button>
                ))}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  aria-label="Vorheriger Monat"
                  onClick={() => shiftMonth(-1)}
                >
                  <ChevronLeftIcon className="size-4" />
                </Button>
                <h2 className="min-w-40 text-center text-lg font-semibold">
                  {monthLabel(year, month)}
                </h2>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  aria-label="Nächster Monat"
                  onClick={() => shiftMonth(1)}
                >
                  <ChevronRightIcon className="size-4" />
                </Button>
              </div>
            )}
            <div className="flex rounded-md border">
              <Button
                type="button"
                variant={mode === 'year' ? 'default' : 'ghost'}
                className="rounded-r-none"
                onClick={() => setMode('year')}
              >
                Jahr
              </Button>
              <Button
                type="button"
                variant={mode === 'month' ? 'default' : 'ghost'}
                className="rounded-l-none"
                onClick={() => setMode('month')}
              >
                Monat
              </Button>
            </div>
            <Button
              type="button"
              variant="outline"
              aria-expanded={filtersOpen}
              onClick={() => setFiltersOpen((open) => !open)}
            >
              {filtersOpen ? 'Filter ausblenden' : 'Filter anzeigen'}
            </Button>
            {range.start ? (
              <Button type="button" variant="ghost" onClick={range.clear}>
                Auswahl aufheben
              </Button>
            ) : null}
          </div>
          {range.start ? (
            <p className="text-sm text-muted-foreground">
              Ende wählen. Beginn: {formatGermanDate(range.start)}.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Zwei Klicks setzen den Zeitraum für einen neuen Antrag.
            </p>
          )}
          {filtersOpen ? (
            <div className={cn(leavePanelClassName, 'flex flex-wrap items-end gap-3')}>
              <div>
                <Label htmlFor="leave-kind-filter">Art</Label>
                <Select
                  value={kindFilter || 'none'}
                  onValueChange={(value) => setKindFilter(value === 'none' ? '' : value)}
                >
                  <SelectTrigger id="leave-kind-filter" className="mt-2 w-56">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Alle Arten</SelectItem>
                    {catalog.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.labelDe}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {mode === 'year' ? (
                <>
                  <div>
                    <Label htmlFor="leave-status-filter">Status</Label>
                    <Select
                      value={yearStatus || 'none'}
                      onValueChange={(value) =>
                        setYearStatus(value === 'none' ? '' : (value as 'approved' | 'submitted'))
                      }
                    >
                      <SelectTrigger id="leave-status-filter" className="mt-2 w-56">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Alle</SelectItem>
                        <SelectItem value="approved">Genehmigt</SelectItem>
                        <SelectItem value="submitted">Eingereicht</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {canViewOtherYear ? (
                    <div>
                      <Label htmlFor="leave-person-filter">Person</Label>
                      <Input
                        id="leave-person-filter"
                        className="mt-2 w-56"
                        value={personQuery}
                        onChange={(event) => setPersonQuery(event.target.value)}
                        placeholder={yearUserName ?? 'Name suchen'}
                      />
                      {yearUserId ? (
                        <button
                          type="button"
                          className="mt-1 text-sm text-primary hover:underline"
                          onClick={() => {
                            setYearUserId(null);
                            setYearUserName(null);
                            setPersonQuery('');
                          }}
                        >
                          Eigene Person
                        </button>
                      ) : null}
                      {people.data && people.data.items.length > 0 ? (
                        <ul className="mt-1 max-h-40 w-56 overflow-auto rounded-md border bg-background text-sm">
                          {people.data.items.map((person) => (
                            <li key={person.id}>
                              <button
                                type="button"
                                className="w-full px-3 py-1.5 text-left hover:bg-muted"
                                onClick={() => {
                                  setYearUserId(person.id);
                                  setYearUserName(person.displayName);
                                  setPersonQuery('');
                                }}
                              >
                                {person.displayName}
                              </button>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  ) : null}
                </>
              ) : (
                <>
                  <div>
                    <Label htmlFor="leave-name-filter">Name</Label>
                    <Input
                      id="leave-name-filter"
                      className="mt-2 w-56"
                      value={nameQuery}
                      onChange={(event) => setNameQuery(event.target.value)}
                      placeholder="Name suchen"
                    />
                  </div>
                  {canSwitchDepartment ? (
                    <div>
                      <Label htmlFor="leave-department-filter">Abteilung</Label>
                      <Select
                        value={departmentId || 'none'}
                        onValueChange={(value) => setDepartmentId(value === 'none' ? '' : value)}
                      >
                        <SelectTrigger id="leave-department-filter" className="mt-2 w-56">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Eigene Abteilung</SelectItem>
                          {departments.data?.items.map((department) => (
                            <SelectItem key={department.id} value={department.id}>
                              {department.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          ) : null}
          <LeaveCalendarLegend types={catalog} />
          {mode === 'year' ? (
            <>
              {yearCalendar.isPending ? (
                <p className="text-sm text-muted-foreground">Kalender wird geladen …</p>
              ) : null}
              {yearData ? (
                <>
                  {schoolAreaOn && schoolSpans.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      Schulferien für dieses Jahr sind nicht hinterlegt.
                    </p>
                  ) : null}
                  {schoolSpans.length > 0 && schoolState ? (
                    <p className="text-sm text-muted-foreground">
                      Schulferien: Deutschland – {schoolState}
                    </p>
                  ) : null}
                  <LeaveYearGrid
                    year={yearData.year}
                    absences={visibleAbsences}
                    holidays={yearData.holidays}
                    schoolHolidays={schoolSpans}
                    today={today}
                    linkRequests={linkOwnRequests}
                    rangeStart={range.start}
                    onPickDate={range.pick}
                  />
                  <LeaveYearMonthList
                    year={yearData.year}
                    absences={visibleAbsences}
                    holidays={yearData.holidays}
                    schoolHolidays={schoolSpans}
                    today={today}
                    linkRequests={linkOwnRequests}
                    rangeStart={range.start}
                    onPickDate={range.pick}
                  />
                  <LeaveYearTotals totals={yearData.totals} />
                </>
              ) : null}
            </>
          ) : (
            <>
              {calendar.isPending ? (
                <p className="text-sm text-muted-foreground">Kalender wird geladen …</p>
              ) : null}
              {calendar.data ? (
                <>
                  <LeaveCalendarMonth
                    year={year}
                    month={month}
                    days={days}
                    today={today}
                    rangeStart={range.start}
                    onPickDate={range.pick}
                  />
                  <LeaveCalendarDayList
                    days={days}
                    today={today}
                    rangeStart={range.start}
                    onPickDate={range.pick}
                    className="lg:hidden"
                  />
                  {emptyMonth ? (
                    <p className="hidden text-sm text-muted-foreground lg:block">
                      Niemand abwesend
                    </p>
                  ) : null}
                </>
              ) : null}
            </>
          )}
        </div>
      )}
    </div>
  );
}
